// The crosstable — the table every published tournament report has and this app did not.
//
// TWO SHAPES, BECAUSE TWO QUESTIONS.
//
//   'grid'   the N-by-N table a round robin is read as: a row and a column per player, and
//            the cell where they cross is their game. It only makes sense when everyone
//            has played, or will play, everyone; in a Swiss it would be a vast mostly
//            empty square.
//   'rounds' one column per round, each cell naming the OPPONENT BY RANK, the colour and
//            the result — "12w1" is "beat rank 12 with White". This is what a Swiss
//            crosstable means, and it is how chess-results, Swiss-Manager and
//            TurneringsService all print one.
//
// RANKS, NOT STARTING NUMBERS, inside the cells. A crosstable is read top to bottom: you
// find a player's row, see "12w1", and look at row 12. Printing the starting number there
// instead would send the reader to a row that is somewhere else entirely, which is the one
// mistake that makes the whole table useless.
//
// Pure: no DOM, no state, no Firestore. It takes the ranking the standings already
// computed (window.TieBreak.compute's `order`) rather than ranking anything itself, so the
// crosstable and the standings can never disagree about who is where. Rendering — names,
// colours, HTML — belongs to js/ui/crosstable.js.

// Above this many players a grid is wider than any screen or sheet of paper, so the
// round-by-round shape is used even for a round robin. A round robin that large does not
// really exist; the guard is here for the tournament that was created as one by mistake.
export const MAX_GRID_PLAYERS = 30;

const ROUND_ROBIN_FORMATS = [
    'rr', 'drr', 'round-robin', 'roundrobin', 'double-rr', 'double-round-robin'
];

function isReal(id) {
    return !!id && id !== 'WO' && id !== 'BYE';
}

/**
 * Which shape suits this tournament.
 *
 * @param {Object} opts { format, playerCount }
 * @returns {'grid'|'rounds'}
 */
export function preferredKind({ format, playerCount } = {}) {
    const rr = ROUND_ROBIN_FORMATS.includes(String(format || '').toLowerCase());
    return rr && (playerCount || 0) <= MAX_GRID_PLAYERS ? 'grid' : 'rounds';
}

/**
 * The glyph for one side of a result. The conventional characters, not the point values:
 * a crosstable states what happened in the game, and the points it was worth are in the
 * row's total. Under a 3-1-0 system a win is still written '1' here and still adds 3 to
 * the total, which is exactly how a football table is read too.
 *
 * @param {string} result  the app's result code
 * @param {boolean} asWhite which side to describe
 * @returns {string} '1' | '½' | '0' | '+' | '-' | '' (game not finished)
 */
export function resultChar(result, asWhite) {
    switch (result) {
        case '1-0': return asWhite ? '1' : '0';
        case '0-1': return asWhite ? '0' : '1';
        case '0.5-0.5': return '½';
        case '+/-': return asWhite ? '+' : '-';
        case '-/+': return asWhite ? '-' : '+';
        case '-/-': return '-';
        default: return '';
    }
}

/** The glyph for a bye, which has only one side. */
function byeChar(result, asWhite) {
    const win = (asWhite && (result === '1-0' || result === '+/-'))
        || (!asWhite && (result === '0-1' || result === '-/+'));
    if (win) return '1';
    if (result === '0.5-0.5') return '½';
    return '0';
}

/**
 * Every game each player played, flattened out of the rounds and indexed by player id.
 *
 * A player can appear twice in one round only through a hand-edited round, and both
 * entries are kept: the crosstable's job is to show what the tournament actually records,
 * not to tidy it into what it should have been.
 */
function gamesByPlayer(players, rounds) {
    const games = {};
    players.forEach(p => { games[p.id] = []; });

    (rounds || []).forEach((round, roundIndex) => {
        (round.pairings || []).forEach(pair => {
            [true, false].forEach(asWhite => {
                const id = asWhite ? pair.white : pair.black;
                if (!isReal(id) || !games[id]) return;
                const oppId = asWhite ? pair.black : pair.white;
                const result = pair.result || '';
                if (isReal(oppId)) {
                    games[id].push({
                        round: roundIndex + 1,
                        opponentId: oppId,
                        colour: asWhite ? 'w' : 'b',
                        char: resultChar(result, asWhite),
                        bye: false
                    });
                } else {
                    games[id].push({
                        round: roundIndex + 1,
                        opponentId: null,
                        colour: '',
                        char: byeChar(result, asWhite),
                        bye: true,
                        // Told apart so the table can say "asked to sit out" rather than
                        // implying the player was the odd one out that round.
                        requested: !!pair.requestedBye
                    });
                }
            });
        });
    });
    return games;
}

/**
 * Build a crosstable.
 *
 * @param {Object} opts
 * @param {Array}  opts.players  the field ({ id, ... })
 * @param {Array}  opts.rounds   rounds played, each { pairings: [...] }
 * @param {Array}  opts.order    the ranked rows from TieBreak.compute — [{ id, rank, points }]
 * @param {('grid'|'rounds')} opts.kind
 * @param {number} [opts.totalRounds] how many round columns to show; defaults to the
 *                 rounds played. Only used by the 'rounds' shape.
 * @returns {{ kind, columns, rows }}
 */
export function buildCrosstable({ players, rounds, order, kind, totalRounds } = {}) {
    players = players || [];
    rounds = rounds || [];
    order = order || [];

    const playerById = {};
    players.forEach(p => { playerById[p.id] = p; });

    // Rank by id, from the ranking the standings produced. A player who somehow is not in
    // it — deleted mid-tournament, say — has no rank and no row, but their GAMES are still
    // in everyone else's rows, shown against '?' rather than silently dropped.
    const rankById = {};
    order.forEach(r => { rankById[r.id] = r.rank; });

    const games = gamesByPlayer(players, rounds);
    const ranked = order.filter(r => playerById[r.id]);

    if (kind === 'grid') return grid(ranked, playerById, games, rankById);
    return roundByRound(ranked, playerById, games, rankById, rounds, totalRounds);
}

function roundByRound(ranked, playerById, games, rankById, rounds, totalRounds) {
    const columnCount = Math.max(parseInt(totalRounds, 10) || 0, rounds.length);
    const columns = Array.from({ length: columnCount }, (_, i) => ({ round: i + 1 }));

    const rows = ranked.map(entry => {
        const cells = Array.from({ length: columnCount }, () => ({ kind: 'none' }));
        (games[entry.id] || []).forEach(g => {
            const slot = g.round - 1;
            if (slot < 0 || slot >= columnCount) return;
            // A second game in the same round (hand-edited rounds do happen) would
            // otherwise overwrite the first; keep the first and let the extra go into the
            // next free column rather than vanishing.
            const target = cells[slot].kind === 'none'
                ? slot
                : cells.findIndex((c, i) => i > slot && c.kind === 'none');
            if (target < 0) return;
            cells[target] = g.bye
                ? { kind: 'bye', char: g.char, requested: g.requested }
                : {
                    kind: 'game',
                    opponentRank: rankById[g.opponentId] || null,
                    opponentId: g.opponentId,
                    colour: g.colour,
                    char: g.char
                };
        });
        return { id: entry.id, rank: entry.rank, points: entry.points, cells };
    });

    return { kind: 'rounds', columns, rows };
}

function grid(ranked, playerById, games, rankById) {
    const columns = ranked.map(entry => ({ rank: entry.rank, id: entry.id }));
    const indexByRank = {};
    ranked.forEach((entry, i) => { indexByRank[entry.rank] = i; });

    const rows = ranked.map(entry => {
        const cells = ranked.map((other) => (
            other.id === entry.id
                ? { kind: 'self', games: [] }
                : { kind: 'none', games: [] }
        ));
        const byes = [];

        (games[entry.id] || []).forEach(g => {
            if (g.bye) {
                byes.push({ round: g.round, char: g.char, requested: g.requested });
                return;
            }
            const rank = rankById[g.opponentId];
            const at = rank == null ? -1 : indexByRank[rank];
            // A game against someone who is no longer in the field has no column to sit
            // in. It is still a game that was played, so it is kept beside the byes rather
            // than dropped — the alternative is a row whose total does not add up.
            if (at == null || at < 0) {
                byes.push({ round: g.round, char: g.char, requested: false, missing: true });
                return;
            }
            cells[at] = {
                kind: 'game',
                games: [...cells[at].games, { round: g.round, colour: g.colour, char: g.char }]
            };
        });

        return { id: entry.id, rank: entry.rank, points: entry.points, cells, byes };
    });

    return { kind: 'grid', columns, rows };
}
